import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const allNavItems = [
  { icon: "ri-dashboard-3-line", label: "Dashboard", href: "/admin" },
  { icon: "ri-article-line", label: "Articles", href: "/admin/articles" },
  { icon: "ri-folder-chart-line", label: "Projects", href: "/admin/projects" },
  { icon: "ri-team-line", label: "Staffs", href: "/admin/users" },
  { icon: "ri-heart-line", label: "Interns & Volunteers", href: "/admin/interns-volunteers" },
  { icon: "ri-settings-3-line", label: "Settings", href: "/admin/settings" },
];

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export default function AdminLayout({ children, title, subtitle }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, canAccess } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = allNavItems.filter((item) => {
    const page = item.href === "/admin" ? "dashboard" : item.href.replace("/admin/", "");
    return canAccess(page);
  });

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const initials = user?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase() ?? "??";

  const roleLabel =
    user?.role === "superadmin"
      ? "Superadmin"
      : user?.role === "administrator"
        ? "Administrator"
        : "Operation Manager";

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-stone-100 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-stone-100">
          <div className="w-9 h-9 flex items-center justify-center bg-primary rounded-xl shrink-0">
            <i className="ri-leaf-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-stone-800 leading-tight">BHODEV</p>
            <p className="text-xs text-stone-400">Admin Panel</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest px-3 mb-3">Main Menu</p>
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => {
              const isActive =
                item.href === "/admin"
                  ? location.pathname === "/admin"
                  : location.pathname.startsWith(item.href);
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                      isActive
                        ? "bg-primary text-white"
                        : "text-stone-600 hover:bg-stone-100 hover:text-stone-800"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom */}
        <div className="px-3 py-4 border-t border-stone-100">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-700 transition-all duration-200 cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-external-link-line text-base"></i>
            </div>
            View Website
          </Link>
          <div className="flex items-center gap-3 px-3 py-3 mt-2 bg-stone-50 rounded-xl">
            <div className="w-8 h-8 flex items-center justify-center bg-primary/10 rounded-full shrink-0">
              <span className="text-xs font-bold text-primary">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-stone-800 truncate">{user?.name ?? "Unknown"}</p>
              <p className="text-xs text-stone-400 truncate">{roleLabel}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
              title="Sign out"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-stone-100 px-4 md:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden w-9 h-9 flex items-center justify-center text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg cursor-pointer transition-colors"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-stone-800 leading-tight">{title}</h1>
            {subtitle && <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/settings")}
              className="w-9 h-9 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg cursor-pointer transition-colors"
            >
              <i className="ri-settings-3-line text-lg"></i>
            </button>
            <button className="relative w-9 h-9 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg cursor-pointer transition-colors">
              <i className="ri-notification-3-line text-lg"></i>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-4 md:px-8 py-6 md:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}